import { useEffect, useState } from 'react';
import { useBranding } from '../../context/BrandingContext';

const TAGLINE_FONT_FAMILIES = {
  normal: "inherit",
  italic: "'Georgia', 'Times New Roman', serif",
  serif:  "'Georgia', 'Times New Roman', serif",
  mono:   "ui-monospace, 'SFMono-Regular', Menlo, monospace",
};

const LOCAL_LOGO_FALLBACK = '/logo192.png';

/**
 * Listens for changes to the documentElement's class attribute and returns
 * the current theme — 'light' or 'dark'. Used to pick the correct logo
 * variant without a full React context dependency.
 */
function useTheme() {
  const [theme, setTheme] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const update = () => setTheme(root.classList.contains('dark') ? 'dark' : 'light');
    update();
    const obs = new MutationObserver(update);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return theme;
}

/**
 * SiteLogo — single source of truth for the logo + wordmark + tagline block.
 * Used in Header (desktop + mobile slide-out), Footer, and anywhere else the
 * brand mark appears. Picks dark-mode logo when the root has the `dark` class.
 *
 * Props:
 *   - variant: 'header' | 'footer' | 'mobile' (controls size + tagline visibility)
 *   - showTagline: override for tagline visibility
 *   - className: extra wrapper classes
 */
export default function SiteLogo({ variant = 'header', showTagline, className = '' }) {
  const { branding } = useBranding();
  const theme = useTheme();

  const isFooter = variant === 'footer';
  const darkSrc = isFooter ? branding.footer_logo_url_dark : branding.logo_url_dark;
  const lightSrc = isFooter ? branding.footer_logo_url : branding.logo_url;
  const src = (theme === 'dark' && darkSrc) ? darkSrc : (lightSrc || branding.logo_url);

  // Branding assets are admin-controlled and can temporarily point at a stale
  // R2/legacy URL. Fall back to the bundled mark while keeping the CCUK
  // wordmark visible so mobile never collapses to an anonymous square icon.
  const [imageSrc, setImageSrc] = useState(src || LOCAL_LOGO_FALLBACK);
  useEffect(() => {
    setImageSrc(src || LOCAL_LOGO_FALLBACK);
  }, [src]);

  const handleImageError = () => {
    if (imageSrc !== LOCAL_LOGO_FALLBACK) {
      setImageSrc(LOCAL_LOGO_FALLBACK);
      return;
    }
    setImageSrc(null);
  };

  const sizeClasses = {
    header: 'h-9 lg:h-11',
    mobile: 'h-8',
    footer: 'h-10',
  }[variant] || 'h-10';

  const wordmarkClasses = {
    // Keep the full CCUK mark + wordmark treatment on phones. Responsive type
    // preserves room for the menu/actions without hiding the brand name.
    header: 'text-[20px] min-[360px]:text-[22px] sm:text-[26px] lg:text-[28px] leading-[0.95]',
    mobile: 'text-xl',
    footer: 'font-bold text-lg',
  }[variant] || 'text-xl';

  const defaultShowTagline = variant === 'header';
  const tagActive = showTagline ?? defaultShowTagline;

  const fontStyle = branding.tagline_font_style || 'normal';
  const taglineStyle = {
    fontFamily: TAGLINE_FONT_FAMILIES[fontStyle] || TAGLINE_FONT_FAMILIES.normal,
    fontStyle: fontStyle === 'italic' ? 'italic' : 'normal',
    fontWeight: branding.tagline_font_weight || '500',
    color: branding.tagline_color || undefined,
    letterSpacing: '0.01em',
  };

  return (
    <div
      className={`flex min-w-0 max-w-full items-center overflow-hidden ${
        variant === 'header' ? 'gap-1.5 sm:gap-2 lg:gap-3' : 'gap-3'
      } ${className}`}
      data-testid={`site-logo-${variant}`}
    >
      {imageSrc && (
        <img
          src={imageSrc}
          alt={branding.site_name || 'Casting Calls UK'}
          onError={handleImageError}
          data-testid={`site-logo-${variant}-brand-image`}
          className={`${sizeClasses} w-auto max-w-full flex-none object-contain`}
          style={{ minWidth: variant === 'header' ? '40px' : '32px' }}
          decoding="async"
        />
      )}

      {branding.site_name && (
        <div className="flex min-w-0 flex-col items-center justify-center overflow-hidden">
          <span
            className={`max-w-full truncate text-theme-text ${wordmarkClasses}`}
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
          >
            {branding.site_name}
          </span>
          {tagActive && (
            <span
              className="mt-0.5 hidden min-h-[14px] max-w-full truncate text-center text-[11px] leading-tight text-theme-muted lg:inline"
              style={taglineStyle}
              data-testid={`${variant}-tagline`}
            >
              {branding.tagline || '\u00A0'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
